import type {ReactElement} from 'react';
import {useContext, useEffect, useRef, useState} from 'react';
import {
  useAccountIdsToPublishTo,
  usePublishingParams,
  usePublishingStatusForAllAccounts,
  useSocialMediaWizardAccounts,
  useSocialMediaWizardPost,
  useSocialPostPublishingAction,
  useWizardStep,
} from '@Components/social-media/social-media.hooks';
import {useGraphicsReadiness, useShowReviewRequestOnPublishSettle} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {CircularProgressLoader, LOADER_COLOR, LOADER_SIZE} from '@Components/circular-progress-loader';
import {Text, TextSize} from '@Components/text';
import {AddAccountIcon} from '@Components/social-media/components/add-account-icon';
import {SocialProfileIcon, SocialProfileIconSize} from '@Components/social-media/components/social-profile-icon';
import type {MediaTypePublishingParams, SocialMediaAccountId} from '@Components/social-media/post.vo';
import {PlatformMediaType, PostState} from '@Components/social-media/post.vo';
import {
  BLURABLE_CLASS_IN_AI_CAPTION_MODE,
  PUBLISH_PARAM_HANDLER_CLASS,
  SocialMediaWizardContentSize,
  SocialMediaWizardStep,
  WizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.types';
import {getKeyForPublishParams} from '@Libraries/social-media-wizard-design-library';
import {MAX_ACCOUNT_BUTTONS_TO_SHOW} from '@Libraries/social-media-wizard-library';
import {SocialProfileIconPlaceholder} from '@Components/social-media-wizard/components/social-profile-icon-placeholder';
import {SocialMediaWizardContainerQueriesContext} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import {usePostId, useWizardMobileNavView} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {SocialMediaMobileFooterType} from '@Components/social-media-wizard/components/social-media-mobile-footer/social-media-mobile-footer.types';
import {noop} from '@Utils/general.util';
import {SocialMediaPostPublishParamsWrapper} from '@Components/social-media-wizard/components/social-media-post-publish-params-wrapper';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {SocialPostPlatformSelectionTransitionEventDetail} from '@Components/social-media/social-media.types';
import {SocialPostPlatformSelectionTransitionEvent} from '@Components/social-media/social-media.types';
import {LoadingStates} from '@Utils/loading.util';
import {
  getNonStoryPlatformMediaType,
  hasPublishingTransitionedFromBothToPostOrReelOnly,
  hasPublishingTransitionedFromBothToStoryOnly,
  hasPublishingTransitionedFromPostOrReelOnlyToBoth,
  hasPublishingTransitionedFromStoryOnlyToBoth,
  havePublishingParamsChangedForAccountWithId,
  isPublishingAsAPostOrReelOnly,
  isPublishingAsPost,
  isPublishingAsPostOrReel,
  isPublishingAsReel,
  isPublishingAsStory,
  isPublishingAsStoryOnly,
} from '@Libraries/social-media-params.library';
import styles from './social-media-post-details.module.scss';
import {buildAutoCaptionPublishingPayload, createAutoCaptionGenerationFunction, hasAccountsAwaitingAutoCaptions} from '@Libraries/auto-caption-library';
import {useAppDispatch} from '@/hooks';
import type {AutoCaptionGenerationResult} from '@Components/ai-text-prompt-box/ai-text-prompt-box.types';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import {
  setAreCaptionsBeingAutoGenerated,
  updateCommonCaption,
  updateHasCommonCaption,
  updatePublishingParamAllText,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {isUserPremium} from '@Libraries/user.library';

interface SocialMediaPostDetailsProps {
  size?: SocialMediaWizardContentSize;
  onClickAddAccount?: VoidFunction;
}

export function SocialMediaPostDetails({size = SocialMediaWizardContentSize.XLARGE, onClickAddAccount = noop}: SocialMediaPostDetailsProps): ReactElement<any> | null {
  const accounts = useSocialMediaWizardAccounts();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const publishingAction = useSocialPostPublishingAction();
  useShowReviewRequestOnPublishSettle();
  const graphicsReadiness = useGraphicsReadiness();
  const wizardMobileNavView = useWizardMobileNavView();
  const wizardPost = useSocialMediaWizardPost();
  const wizardStep = useWizardStep();
  const publishingDetails: ReactElement<any>[] = [];
  const selectedAccountsTabs: ReactElement<any>[] = [];
  const publishParamHandlerClass = PUBLISH_PARAM_HANDLER_CLASS;
  const allPublishingParams = usePublishingParams();
  const publishingStatus = usePublishingStatusForAllAccounts();
  const {isSmallScreen, isExtraSmallScreen, isMediumScreen, isExtraLargeScreen, isLargeScreen} = useContext(SocialMediaWizardContainerQueriesContext);
  const postId = usePostId();
  const dispatch = useAppDispatch();
  const isAutoCaptionGenerationInFlightRef = useRef(false);

  let firstAccountId: SocialMediaAccountId = 0;
  let firstPublishingParamMediaType = PlatformMediaType.POST;

  if (accountIdsToPublishTo.length > 0) {
    [firstAccountId] = accountIdsToPublishTo;
    // eslint-disable-next-line
    // @ts-ignore
    firstPublishingParamMediaType =
      getNonStoryPlatformMediaType(allPublishingParams[firstAccountId]) !== null ? getNonStoryPlatformMediaType(allPublishingParams[firstAccountId]) : PlatformMediaType.STORIES;
  }

  const [selectedAccountId, setSelectedAccountId] = useState(firstAccountId);
  const [selectedPublishingParamMediaType, setSelectedPublishingParamMediaType] = useState(firstPublishingParamMediaType);
  const [allPublishingParamState, setAllPublishingParamsState] = useState<Record<SocialMediaAccountId, MediaTypePublishingParams>>({});

  const firePlatformSelectionTransitionEvent = (accountId: number, platformSelection: PlatformMediaType): void => {
    window.dispatchEvent(
      new CustomEvent(SocialPostPlatformSelectionTransitionEvent, {
        detail: {
          accountId,
          platformSelection,
        } as SocialPostPlatformSelectionTransitionEventDetail,
      })
    );
  };

  useEffect(() => {
    void generateAutoCaptionsForAccountsAwaitingThem();
  }, [accountIdsToPublishTo, postId]);

  const generateAutoCaptionsForAccountsAwaitingThem = async (): Promise<void> => {
    if (!isUserPremium() || !postId || isAutoCaptionGenerationInFlightRef.current) {
      return;
    }

    if (!hasAccountsAwaitingAutoCaptions(accountIdsToPublishTo, allPublishingParams)) {
      return;
    }

    isAutoCaptionGenerationInFlightRef.current = true;
    try {
      await generateAndApplyAutoCaptions(postId);
    } finally {
      isAutoCaptionGenerationInFlightRef.current = false;
    }
  };

  const generateAndApplyAutoCaptions = async (postIdToGenerateFor: string): Promise<void> => {
    dispatch(setAreCaptionsBeingAutoGenerated(true));

    try {
      const autoCaptionFunction = createAutoCaptionGenerationFunction(getAccountsToPublishTo(), postIdToGenerateFor);
      const response = await autoCaptionFunction();

      if (response) {
        updatePublishingParamsAfterAutoGeneration(response);
      }
    } finally {
      dispatch(setAreCaptionsBeingAutoGenerated(false));
    }
  };

  const getAccountsToPublishTo = (): SocialAccount[] => {
    return accounts.filter((account) => {
      return accountIdsToPublishTo.includes(account.id);
    });
  };

  const updatePublishingParamsAfterAutoGeneration = (result: AutoCaptionGenerationResult): void => {
    const payload = buildAutoCaptionPublishingPayload(result, accountIdsToPublishTo, allPublishingParams);

    if (Object.keys(payload).length === 0) {
      return;
    }

    if (result.hasMultiplePlatformCaptions) {
      dispatch(updateHasCommonCaption(false));
    }

    dispatch(updatePublishingParamAllText(payload));

    if (!result.hasMultiplePlatformCaptions) {
      const [onlyGeneratedCaption] = Object.values(result.captionsByAccountId);
      dispatch(updateCommonCaption(onlyGeneratedCaption.caption));
    }
  };

  useEffect(() => {
    if (Object.keys(allPublishingParamState).length === 0) {
      setAllPublishingParamsState(allPublishingParams);
      return;
    }

    for (let i = 0; i < accountIdsToPublishTo.length; i++) {
      if (havePublishingParamsChangedForAccountWithId(allPublishingParamState, allPublishingParams, accountIdsToPublishTo[i])) {
        if (hasPublishingTransitionedFromPostOrReelOnlyToBoth(allPublishingParamState, allPublishingParams, accountIdsToPublishTo[i])) {
          firePlatformSelectionTransitionEvent(accountIdsToPublishTo[i], PlatformMediaType.STORIES);
          scrollToAccountSection(accountIdsToPublishTo[i], PlatformMediaType.STORIES);
          break;
        }

        if (hasPublishingTransitionedFromStoryOnlyToBoth(allPublishingParamState, allPublishingParams, accountIdsToPublishTo[i])) {
          const platformMediaType = allPublishingParams[accountIdsToPublishTo[i]].POST ? PlatformMediaType.POST : PlatformMediaType.REELS;
          firePlatformSelectionTransitionEvent(accountIdsToPublishTo[i], platformMediaType);
          scrollToAccountSection(accountIdsToPublishTo[i], platformMediaType);
          break;
        }

        if (hasPublishingTransitionedFromBothToPostOrReelOnly(allPublishingParamState, allPublishingParams, accountIdsToPublishTo[i])) {
          const platformMediaType = allPublishingParams[accountIdsToPublishTo[i]].POST ? PlatformMediaType.POST : PlatformMediaType.REELS;
          firePlatformSelectionTransitionEvent(accountIdsToPublishTo[i], platformMediaType);
          scrollToAccountSection(accountIdsToPublishTo[i], platformMediaType);
          break;
        }

        if (hasPublishingTransitionedFromBothToStoryOnly(allPublishingParamState, allPublishingParams, accountIdsToPublishTo[i])) {
          firePlatformSelectionTransitionEvent(accountIdsToPublishTo[i], PlatformMediaType.STORIES);
          scrollToAccountSection(accountIdsToPublishTo[i], PlatformMediaType.STORIES);
          break;
        }
      }
    }

    setAllPublishingParamsState(allPublishingParams);
  }, [allPublishingParams]);

  const getMediaTypeFromPublishingParamIndexForAccount = (
    accountId: number,
    index: number,
    updatedPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>
  ): PlatformMediaType => {
    if (isPublishingAsStoryOnly(updatedPublishingParams, accountId)) {
      return PlatformMediaType.STORIES;
    }

    if (isPublishingAsAPostOrReelOnly(updatedPublishingParams, accountId)) {
      if (isPublishingAsPost(updatedPublishingParams, accountId)) {
        return PlatformMediaType.POST;
      }
      return PlatformMediaType.REELS;
    }

    // implies publishing to BOTH
    if (index === 0 && isPublishingAsPost(updatedPublishingParams, accountId)) {
      return PlatformMediaType.POST;
    }

    if (index === 0 && isPublishingAsReel(updatedPublishingParams, accountId)) {
      return PlatformMediaType.REELS;
    }

    return PlatformMediaType.STORIES;
  };

  const onPageScroll = (updatedPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>): void => {
    const allWrappers = document.querySelectorAll(`.${publishParamHandlerClass}`);
    const getMaxVisibleElementInfo = (wrappers: NodeListOf<Element>, updatedPublishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>) => {
      let maxVisibleHeight = 0;
      let maxVisibleInfo = {accountId: -1, publishingParamIndex: -1, mediaType: PlatformMediaType.POST};

      wrappers.forEach((wrapper) => {
        const accountId = wrapper.getAttribute('data-id-account');
        const {children} = wrapper;

        for (let i = 0; i < children.length; i++) {
          const child = children[i];
          const rect = child.getBoundingClientRect();
          const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

          if (visibleHeight > maxVisibleHeight) {
            maxVisibleHeight = visibleHeight;
            const accountIdParsed = accountId ? parseInt(accountId, 10) : -1;
            const paramsIndex = i === 0 ? 0 : 1;
            maxVisibleInfo = {
              accountId: accountIdParsed,
              publishingParamIndex: paramsIndex,
              mediaType: getMediaTypeFromPublishingParamIndexForAccount(accountIdParsed, paramsIndex, updatedPublishingParams),
            };
          }
        }
      });

      return maxVisibleInfo;
    };

    const maxVisibleElementInfo = getMaxVisibleElementInfo(allWrappers, updatedPublishingParams);

    if (maxVisibleElementInfo.accountId !== -1) {
      setSelectedAccountId(maxVisibleElementInfo.accountId);
      setSelectedPublishingParamMediaType(maxVisibleElementInfo.mediaType);
    }
  };

  const getFooterType = (): SocialMediaMobileFooterType => {
    if (wizardStep === SocialMediaWizardStep.ADD_DETAILS) {
      if (wizardPost?.publishOn && wizardPost.timeZoneOffset !== null && wizardPost.state === PostState.SCHEDULE_READY) {
        return SocialMediaMobileFooterType.SCHEDULE_STEP;
      }

      return SocialMediaMobileFooterType.PUBLISH_STEP;
    }

    return SocialMediaMobileFooterType.NAVIGATE_STEP;
  };

  const isAccountWithIdOnlyPublishingToSingleMediaType = (accountId: SocialMediaAccountId): boolean => {
    return !(isPublishingAsPostOrReel(allPublishingParams, accountId) && isPublishingAsStory(allPublishingParams, accountId));
  };

  const getPublishingParamContainerForPlatformMediaType = (parentElement: Element, platformMediaType: PlatformMediaType): Element | null => {
    return parentElement.querySelector(`div[data-platform-media-type="${platformMediaType}"]`);
  };

  const scrollToAccountSection = (accountId: SocialMediaAccountId, platformMediaType: PlatformMediaType): void => {
    const yOffset = -100;
    const allParams = document.querySelectorAll(`.${publishParamHandlerClass}`);
    const firstParamId = allParams[0].getAttribute('data-id-account');

    if (firstParamId) {
      const firstAccId = parseInt(firstParamId, 10);

      if (firstAccId === accountId && isAccountWithIdOnlyPublishingToSingleMediaType(accountId)) {
        scrollToTop();
        return;
      }
    }

    const element = document.querySelectorAll(`.${publishParamHandlerClass}[data-key="${getKeyForPublishParams(accountId)}"]`)[0];

    let publishingParamContainerForMediaType = getPublishingParamContainerForPlatformMediaType(element, platformMediaType);

    publishingParamContainerForMediaType ??= element;

    const y = publishingParamContainerForMediaType.getBoundingClientRect().top + yOffset + window.scrollY;

    window.scrollTo({top: y, behavior: 'smooth'});
  };

  const scrollToTop = (): void => {
    window.scrollTo({top: 0, behavior: 'smooth'});
  };

  const moveToSelectAccountsStep = (): void => {
    onClickAddAccount();
  };

  const getPublishingParamsContainerMargin = (): string => {
    if (isExtraSmallScreen || isSmallScreen) {
      if (wizardMobileNavView === WizardMobileNavView.CONTENT) {
        return `spacing-m-b-8`;
      }
    }

    if (isMediumScreen || isLargeScreen || isExtraLargeScreen) {
      return `spacing-m-b-10`;
    }

    return `spacing-m-b-4`;
  };

  const getTerminatingContainerMargin = (): string => {
    const footerType = getFooterType();

    if (isExtraSmallScreen || isSmallScreen) {
      if (footerType === SocialMediaMobileFooterType.SCHEDULE_STEP) {
        return `spacing-m-b-13`;
      }
      return ``;
    }
    return styles.lastPublishParamsContainer;
  };

  useEffect(() => {
    const handleScroll = (): void => {
      onPageScroll(allPublishingParams);
    };

    window.addEventListener('scroll', handleScroll, {passive: true});

    return (): void => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [allPublishingParams]);

  const getSocialProfileIconForAccountWithMediaType = (account: SocialAccount, platformMediaType: PlatformMediaType): ReactElement<any> => {
    return (
      <SocialProfileIcon
        account={account}
        size={SocialProfileIconSize.MEDIUM}
        showHoverOverlay={false}
        platformMediaType={platformMediaType}
        onClick={(): void => {
          scrollToAccountSection(account.id, platformMediaType);
        }}
      />
    );
  };

  const isStorySelected = (): boolean => {
    return selectedPublishingParamMediaType === PlatformMediaType.STORIES;
  };

  const getProfileIconsForAccountId = (account: SocialAccount): ReactElement<any> => {
    const containerForAccounts: ReactElement<any>[] = [];
    const isPost = isPublishingAsPost(allPublishingParams, account.id);

    if (isPublishingAsPostOrReel(allPublishingParams, account.id)) {
      const isPublishError = hasPublishingErroredOutForAccountWithMediaType(account.id, isPost ? PlatformMediaType.POST : PlatformMediaType.REELS);
      const isSelected = selectedPublishingParamMediaType === PlatformMediaType.POST || selectedPublishingParamMediaType === PlatformMediaType.REELS;

      containerForAccounts.push(
        <div
          className={`${styles.accountTab} ${selectedAccountId && selectedAccountId === account.id && isSelected ? styles.tabSelected : ''} ${isPublishError ? styles.tabDanger : ''}`}
          key={`social-profile-icon-${account.id}-post`}
        >
          {getSocialProfileIconForAccountWithMediaType(account, isPublishingAsPost(allPublishingParams, account.id) ? PlatformMediaType.POST : PlatformMediaType.REELS)}
        </div>
      );
    }

    if (isPublishingAsStory(allPublishingParams, account.id)) {
      const isPublishError = hasPublishingErroredOutForAccountWithMediaType(account.id, PlatformMediaType.STORIES);

      containerForAccounts.push(
        <div
          className={`${styles.accountTab} ${selectedAccountId && selectedAccountId === account.id && isStorySelected() ? styles.tabSelected : ''} ${isPublishError ? styles.tabDanger : ''}`}
          key={`social-profile-icon-${account.id}-story`}
        >
          {getSocialProfileIconForAccountWithMediaType(account, PlatformMediaType.STORIES)}
        </div>
      );
    }

    return <>{containerForAccounts}</>;
  };

  const hasPublishingErroredOutForAccountWithMediaType = (accountId: SocialMediaAccountId, platformMediaType: PlatformMediaType): boolean => {
    const mediaTypeLoadingStates = publishingStatus[accountId];
    const loadingStateForPlatform = mediaTypeLoadingStates[platformMediaType];

    return loadingStateForPlatform === LoadingStates.NOT_LOADED;
  };

  const getPublishingParamsContainerClasses = (containerIndex: number): string => {
    return `
      ${containerIndex === 1 ? 'first-publishing-param-container' : ''} 
      ${containerIndex === nAccounts ? getTerminatingContainerMargin() : getPublishingParamsContainerMargin()} 
      ${publishParamHandlerClass}
    `;
  };

  let hasAppendedPlaceholder = false;
  let numButtonsHidden = 0;
  const nAccounts = accountIdsToPublishTo.length;
  let counter = 0;

  accounts.forEach((account, _index) => {
    if (accountIdsToPublishTo.includes(account.id)) {
      counter += 1;
      publishingDetails.push(
        <div
          key={getKeyForPublishParams(account.id)}
          className={getPublishingParamsContainerClasses(counter)}
          data-id-account={account.id}
          data-key={getKeyForPublishParams(account.id)}
        >
          <SocialMediaPostPublishParamsWrapper size={size} key={`id-account-${account.id}`} account={account} />
        </div>
      );

      if (selectedAccountsTabs.length >= MAX_ACCOUNT_BUTTONS_TO_SHOW) {
        numButtonsHidden += 1;
        hasAppendedPlaceholder = true;
      } else {
        const profileIcons = getProfileIconsForAccountId(account);
        selectedAccountsTabs.push(profileIcons);
      }
    }
  });

  if (hasAppendedPlaceholder) {
    selectedAccountsTabs.push(
      <SocialProfileIconPlaceholder
        key={`social-account-icon-placeholder-${counter}`}
        className={`${styles.addAccountSpacer} spacing-m-b-4`}
        countToShow={numButtonsHidden}
        size={SocialProfileIconSize.MEDIUM}
      />
    );
  }

  if (publishingAction === undefined) {
    selectedAccountsTabs.push(<AddAccountIcon key={`add-account-icon-${counter + 1}`} className={styles.addAccountSpacer} onClick={moveToSelectAccountsStep} />);
  }

  const renderPreparingDesignState = (): ReactElement<any> | null => {
    if (publishingAction !== SocialPostPublishingAction.PUBLISH || graphicsReadiness !== LoadingStates.LOADING) {
      return null;
    }

    return (
      <div className="flex-h-row flex-align-items-center flex-justify-center spacing-p-4">
        <CircularProgressLoader size={LOADER_SIZE.SMALL} color={LOADER_COLOR.PRIMARY} classes="spacing-m-l-0 spacing-m-r-2" />
        <Text val={window.i18next.t('pmwjs_preparing_your_design')} size={TextSize.XSMALL} />
      </div>
    );
  };

  return (
    <div>
      <div
        id="account-tabs-onboarding-container"
        className={`
          ${styles.accountTabsContainer}
          ${BLURABLE_CLASS_IN_AI_CAPTION_MODE}
          ${size === SocialMediaWizardContentSize.SMALL || size === SocialMediaWizardContentSize.XSMALL ? '_hidden' : ''}
        `}
      >
        {selectedAccountsTabs}
      </div>
      {renderPreparingDesignState()}
      {publishingDetails}
    </div>
  );
}
